import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';
import DashboardLayout from './components/DashboardLayout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PasswordRecoveryRequestPage from './pages/PasswordRecoveryRequestPage';
import PasswordResetPage from './pages/PasswordResetPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import ServicesPage from './pages/ServicesPage';
import ProductsPage from './pages/ProductsPage';
import InvoicesPage from './pages/InvoicesPage';
import ExpensesPage from './pages/ExpensesPage';
import DonationsPage from './pages/DonationsPage';
import PdvPage from './pages/PdvPage';
import AgendasPage from './pages/AgendasPage';
import CustomersPage from './pages/CustomersPage';
import TerritoriesPage from './pages/TerritoriesPage';
import CustomerTerritoriesPage from './pages/CustomerTerritoriesPage';
import VisitConfigsPage from './pages/VisitConfigsPage';
import StockPage from './pages/StockPage';
import SellersPage from './pages/SellersPage';
import PdvLocationsPage from './pages/PdvLocationsPage';
import MyDayPage from './pages/MyDayPage';
import { Toaster } from 'sonner';
import '@/App.css';

function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/password-recovery" element={<PasswordRecoveryRequestPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Admin Only */}
        <Route
          path="users"
          element={
            <ProtectedRoute requireAdmin>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <ProtectedRoute requireAdmin>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="donations"
          element={
            <ProtectedRoute requireAdmin>
              <DonationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="pdv" element={<PdvPage />} />
        <Route
          path="customers"
          element={
            <ProtectedRoute requireAdmin>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="carteiras"
          element={
            <ProtectedRoute requireAdmin>
              <TerritoriesPage />
            </ProtectedRoute>
          }
        />
        <Route path="territories" element={<Navigate to="/carteiras" replace />} />
        <Route
          path="customer-territories"
          element={
            <ProtectedRoute requireAdmin>
              <CustomerTerritoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock"
          element={
            <ProtectedRoute requireAdmin>
              <StockPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pdv-locations"
          element={
            <ProtectedRoute requireAdmin>
              <PdvLocationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="itinerant-stock" element={<Navigate to="/stock" replace />} />
        <Route
          path="vendedores"
          element={
            <ProtectedRoute requireAdmin>
              <SellersPage />
            </ProtectedRoute>
          }
        />

        {/* Admin + User */}
        <Route path="services" element={<ServicesPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="agendas" element={<AgendasPage />} />
        <Route path="meu-dia" element={<MyDayPage />} />
        <Route path="visit-configs" element={<VisitConfigsPage />} />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
